import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Link } from 'react-router-dom';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { OpenDowngradeFacebookPageModalParams } from '@mm/state/modules/modalsState/actions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { ModalInstanceProps } from '../interfaces';
import { ModalBody, ModalFooter, ModalHeader } from '../modal-helpers';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import { SettingsRoutes } from '../../../routes/settings-routes';

type OuterProps = ModalInstanceProps & OpenDowngradeFacebookPageModalParams;

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const BOTTOM_TEXT = `
  In the meantime, you may stop your Chatbot from responding by deactivating it from the Chatbot menu on the Settings page.
  If you are attempting to disconnect and reconnect your page to troubleshoot an issue, please use the Refresh
  Connection button.
`;

const DowngradeFacebookPageModal = (props: Props) => {
  const { closeModal, companyId, page, downgradeType } = props;
  const isDisconnectingType = downgradeType === 'disconnecting';
  const downgradeMessageTypeText = isDisconnectingType
    ? 'disconnecting'
    : 'transferring';
  const downgradePendingMessageType = isDisconnectingType
    ? 'disconnect'
    : 'transfer';

  const subscriptionName = page.plan ? page.plan.name : 'Unknown';

  return (
    <div className="downgrade-facebook-page-modal">
      <ModalHeader classDescriptionName="disconnect-page-modal__header">
        Downgrade {page.name}
      </ModalHeader>
      <ModalBody className="disconnect-page-modal__body">
        {!page.downgradePending && page.redemption && (
          <p>
            This Facebook Page has a promo code associated with it. If you{' '}
            {downgradePendingMessageType} the page with the promo code still
            attached, it will be lost forever. You may be able to disconnect the
            promo code from your
            <Link
              onClick={closeModal}
              to={SettingsRoutes.billing.getLink()}
              className="disconnect-page-modal__link"
              style={{ paddingRight: 0 }}
            >
              Settings
            </Link>
            .
          </p>
        )}

        {!page.downgradePending && page.subscription && (
          <p>
            This Facebook Page has an associated {subscriptionName}{' '}
            Subscription. You must
            <Link
              onClick={closeModal}
              to={SettingsRoutes.billing.getLink()}
              className="disconnect-page-modal__link"
            >
              downgrade the page to Free
            </Link>
            before {downgradeMessageTypeText}.
          </p>
        )}

        {page.downgradePending && (
          <p>
            This Facebook Page has an open downgrade request with customer
            service. Until this request is processed you cannot{' '}
            {downgradePendingMessageType} this page.
          </p>
        )}

        <p>{BOTTOM_TEXT}</p>
      </ModalBody>
      <ModalFooter>
        <Button onClick={closeModal}>Ok</Button>
      </ModalFooter>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  deleteConnection: (
    connectedPage: ConnectedPageModel,
    companyId: number,
    paginationPage,
  ) =>
    dispatch(
      connectedPageCRUDActions.delete(connectedPage, companyId, paginationPage),
    ),
});

const mapStateToProps = (state: StoreState) => ({
  paginationPage: state.view.activeDashboardPaginationPage,
  companyId: selectActiveCompanyId(state),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(DowngradeFacebookPageModal);
